// src/components/admin/Messages.tsx
import React, { useEffect, useMemo, useState } from "react";

/* ========= API ベース ========= */
const API_BASE = (
  (process as any).env?.REACT_APP_API_BASE_URL ||
  (import.meta as any).env?.VITE_API_BASE_URL ||
  "http://localhost:4000"
).replace(/\/+$/, "");

const API_ADMIN = `${API_BASE}/api/admin`;

/* ========= 型定義（server/index.js の contacts.json に準拠） ========= */
type ContactRecord = {
  id: string;
  userId?: number | string | null;
  userName?: string;
  email?: string;
  category?: string;
  message?: string;
  isRead?: boolean;
  createdAt?: string;
};

function formatDate(value?: string | null): string {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}/${mm}/${dd} ${hh}:${mi}`;
}

const Messages: React.FC = () => {
  const [items, setItems] = useState<ContactRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [error, setError] = useState("");

  /* ========= 一覧取得 ========= */
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(`${API_ADMIN}/contacts`, {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`お問い合わせ一覧の取得に失敗しました（${res.status}）`);
        }
        const data: ContactRecord[] = await res.json();
        // createdAt 降順
        data.sort((a, b) => {
          const ta = new Date(a.createdAt || 0).getTime();
          const tb = new Date(b.createdAt || 0).getTime();
          return tb - ta;
        });
        setItems(data);
        if (data.length > 0) {
          setSelectedId((prev) => prev || data[0].id);
        } else {
          setSelectedId(null);
        }
      } catch (e: any) {
        setError(e?.message || "お問い合わせ一覧の取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  /* ========= 選択中レコード ========= */
  const selected = useMemo(
    () => items.find((x) => x.id === selectedId) || null,
    [items, selectedId]
  );

  /* ========= フィルタ済み一覧 ========= */
  const filtered = useMemo(() => {
    const base =
      filter === "unread"
        ? items.filter((x) => x.isRead === false)
        : items;
    return base.slice().sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [items, filter]);

  return (
    <div
      style={{
        display: "flex",
        height: "90vh",
        background: "#fff",
        borderRadius: 8,
        overflow: "hidden",
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
      }}
    >
      {/* 左：お問い合わせ一覧 */}
      <div
        style={{
          width: 320,
          borderRight: "1px solid #e2e8f0",
          background: "#f8fafc",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: 12,
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14 }}>
            お問い合わせ一覧
          </span>
          <div
            style={{
              display: "inline-flex",
              borderRadius: 999,
              background: "#edf2f7",
              padding: 2,
              gap: 2,
            }}
          >
            <button
              type="button"
              onClick={() => setFilter("all")}
              style={{
                border: "none",
                borderRadius: 999,
                padding: "3px 8px",
                fontSize: 11,
                cursor: "pointer",
                background:
                  filter === "all" ? "#3182ce" : "transparent",
                color: filter === "all" ? "#fff" : "#4a5568",
                fontWeight: filter === "all" ? 700 : 500,
              }}
            >
              すべて
            </button>
            <button
              type="button"
              onClick={() => setFilter("unread")}
              style={{
                border: "none",
                borderRadius: 999,
                padding: "3px 8px",
                fontSize: 11,
                cursor: "pointer",
                background:
                  filter === "unread" ? "#3182ce" : "transparent",
                color: filter === "unread" ? "#fff" : "#4a5568",
                fontWeight: filter === "unread" ? 700 : 500,
              }}
            >
              未読のみ
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ padding: 12, fontSize: 12, color: "#4a5568" }}>
            読み込み中…
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ padding: 12, fontSize: 12, color: "#718096" }}>
            お問い合わせはまだありません。
          </div>
        )}

        <div style={{ flex: 1, overflowY: "auto" }}>
          {filtered.map((c) => (
            <div
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              style={{
                padding: 12,
                borderBottom: "1px solid #e2e8f0",
                cursor: "pointer",
                background:
                  selectedId === c.id ? "#e3f2ff" : "transparent",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#1a202c",
                  }}
                >
                  {c.userName || "（名前なし）"}
                </div>
                {c.userId && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      color: "#4a5568",
                    }}
                  >
                    ID: {c.userId}
                  </span>
                )}
                {c.isRead === false && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 11,
                      background: "#e53e3e",
                      color: "#fff",
                      borderRadius: 999,
                      padding: "2px 8px",
                      fontWeight: 700,
                    }}
                  >
                    未読
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#718096",
                  marginBottom: 4,
                }}
              >
                {c.category || "種別未設定"}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#4a5568",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {c.message || ""}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  color: "#a0aec0",
                }}
              >
                {formatDate(c.createdAt)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 中央：本文表示エリア */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          background: "#f7fafc",
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #e2e8f0",
            background: "#fafbfc",
          }}
        >
          {selected ? (
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#1a202c",
                }}
              >
                {selected.userName || "（名前なし）"}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#4a5568",
                  marginTop: 4,
                }}
              >
                {selected.category || "種別未設定"} /{" "}
                {formatDate(selected.createdAt)}
              </div>
            </div>
          ) : (
            <span style={{ fontSize: 13, color: "#718096" }}>
              左の一覧からお問い合わせを選択してください。
            </span>
          )}
        </div>

        <div
          style={{
            flex: 1,
            padding: 16,
            overflowY: "auto",
          }}
        >
          {selected ? (
            <div
              style={{
                padding: 16,
                borderRadius: 8,
                background: "#ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                fontSize: 13,
                color: "#1a202c",
                whiteSpace: "pre-wrap",
              }}
            >
              {selected.message || "本文は登録されていません。"}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#718096" }}>
              お問い合わせを選択すると、ここに本文が表示されます。
            </div>
          )}
        </div>

        {/* 下部：返信欄は廃止（Outlook側で対応） */}
        <div
          style={{
            padding: 10,
            borderTop: "1px solid #e2e8f0",
            background: "#fff",
            fontSize: 11,
            color: "#a0aec0",
            textAlign: "right",
          }}
        >
          返信は support@boat-okuman.comから行ってください
        </div>
      </div>

      {/* 右：ユーザー情報パネル */}
      <div
        style={{
          width: 260,
          borderLeft: "1px solid #e2e8f0",
          background: "#f7fafc",
          padding: 14,
          fontSize: 12,
        }}
      >
        <h3
          style={{
            margin: "4px 0 8px",
            fontSize: 13,
            fontWeight: 700,
            color: "#2d3748",
          }}
        >
          ユーザー情報
        </h3>

        {selected ? (
          <>
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: "#4a5568" }}>名前</div>
              <div style={{ fontWeight: 700 }}>
                {selected.userName || "（名前なし）"}
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ color: "#4a5568" }}>会員ID</div>
              <div>{selected.userId || "未ログイン"}</div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ color: "#4a5568" }}>メールアドレス</div>
              <div>{selected.email || "未登録"}</div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ color: "#4a5568" }}>カテゴリ</div>
              <div>{selected.category || "種別未設定"}</div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ color: "#4a5568" }}>お問い合わせ日時</div>
              <div>{formatDate(selected.createdAt)}</div>
            </div>

            <div style={{ marginTop: 12, fontSize: 11, color: "#a0aec0" }}>
              ※ 返信は登録メールアドレス宛に、
              support@boat-okuman.comから行ってください。
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: "#718096" }}>
            左の一覧からお問い合わせを選択してください。
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fed7d7",
            color: "#742a2a",
            padding: "6px 12px",
            borderRadius: 8,
            fontSize: 12,
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
            zIndex: 10,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default Messages;
